<template>
  <div class="change-plan-step2 p-24 planes">
    <div class="w100 overflow-auto">
      <div class="change-plan-dialog__esitmate-header-wrapper mb-8">
        <span
          :class="[
            'change-plan-dialog__esitmate-header',
            'change-plan-dialog__esitmate-header--caption',
          ]"
          >{{ t('Selected Plan') }}:</span
        >
        <span
          :class="[
            'change-plan-dialog__esitmate-header',
            'change-plan-dialog__esitmate-header--name',
          ]"
          >{{ selected.name }}</span
        >
      </div>

      <template v-if="selected.id === 'basic' || selected.id === 'free'">
        <ui-info>
          {{
            t('The subscription change is scheduled on {0}', [
              formatBillingDate(nextBillingAt),
            ])
          }}
        </ui-info>
        <div class="change-plan-dialog__esitmate-subtitle mt-8">
          {{
            t(
              'You will not be charged for the next billing period, unless there will be the overage on your current paid plan at the end of billing period.',
            )
          }}
        </div>
      </template>
      <template v-else>
        <ui-info>
          {{ t('The payment amount will be charged automatically each month.') }} <br />
          {{
            t('Subsequent payment is scheduled upon renewal on {0}', [
              formatBillingDate(nextBillingAt),
            ])
          }}
        </ui-info>

        <p class="mt-8">
          {{ t('To switch to the selected plan the following amount should be paid') }}
        </p>

        <div class="row">
          <div class="cell">
            {{ t('Prorated cost') }} ({{ formatBillingDate(estimate.created_at) }} -
            {{ formatBillingDate(nextBillingAt) }})
          </div>
          <div class="cell">
            ${{ numeral(invoiceEstimate.total / 100).format('0,0[.]00') }}
          </div>
        </div>

        <ui-info
          color="red"
          class="mb-8 mt-16"
          v-if="estimate.estimateType === 'lessThan50cents'"
        >
          {{
            t(
              'Due to the limitation of billing system the payment less than $0.5 can not be accepted. For assistance contact the team via live chat.',
            )
          }}
        </ui-info>

        <div class="row" v-if="estimate.invoice_estimate">
          <div class="cell">
            {{ t('Cost on renewal') }} ({{ formatBillingDate(nextBillingAt) }} -
            {{ formatBillingDate(nextBillingEnd) }})
          </div>
          <div class="cell">
            ${{ numeral(selected.price / 100).format('0,0.00') }}
          </div>
        </div>

        <div class="row-title" v-if="addonsStay.length">
          <div class="cell">
            <b>{{ t('Previously purchased add-ons') }}</b>
          </div>
          <div class="cell"></div>
        </div>

        <template v-for="addon in addonsStay">
          <div class="row">
            <div class="cell">{{ planLimitsKeyText[addon.addonName] }}</div>
            <div class="cell">
              ${{ numeral(addon.addonTotalPrice / 100).format('0,0.00') }}
            </div>
          </div>
        </template>

        <div class="row-title" v-if="addonsDeactivated.length">
          <div class="cell">
            <b>{{
              t('Deactivated Add-ons (will be switched-off as included in plan)')
            }}</b>
            <ui-tooltip-icon
              class="ml-4"
              preset="help"
              :text="
                t(
                  'The previously purchased add-ons listed in this section will be switched off to avoid the duplicated charges.<br><br>These add-ons will be included in the plan to which you’re moving.',
                )
              "
            ></ui-tooltip-icon>
          </div>
          <div class="cell"></div>
        </div>

        <template v-for="addon in addonsDeactivated">
          <div class="row">
            <div class="cell">{{ planLimitsKeyText[addon.addonName] }}</div>
            <div class="cell">
              ${{ numeral(addon.addonTotalPrice / 100).format('0,0.00') }}
            </div>
          </div>
        </template>

        <div class="row" v-for="item in invoiceEstimate.discounts" :key="item">
          <div class="cell">{{ t('Discount') }} - {{ item.description }}</div>
          <div class="cell">
            $ {{ numeral((-1 * item.amount) / 100).format(currencyFormat) }}
          </div>
        </div>

        <div class="row">
          <div class="cell">
            <b
              v-html="
                t('Total cost (should be paid on {0})', [formatBillingDate(nextBillingAt)])
              "
            ></b>
          </div>
          <div class="cell">
            ${{ numeral(totalShouldBePaidOn / 100).format('0,0.00') }}
          </div>
        </div>
        <div class="row" v-if="estimate.invoice_estimate">
          <div class="cell">
            <b>{{ t('Total now') }}</b>
          </div>
          <div class="cell">
            ${{ numeral(invoiceEstimate.total / 100).format('0,0.00') }}
          </div>
        </div>

        <div class="mt-16">
          <b>{{
            t(
              'The window for payment will appear soon after you click on Confirm Payment.',
            )
          }}</b>
        </div>
        <div
          v-html="
            t(
              '<p>If 3DS security is enabled for your card you will see the payment window again for additional authorization. <br>In the appeared window click on <b>Confirm 3DS and pay</b> to access the BeMob panel. For questions contact support in live chat or a support@bemob.com</p>',
            )
          "
        ></div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import numeral from 'numeral';
import { useI18n, usePlanLimits } from '@/composables';
import {
  formatBillingDate,
  getNextBillingAt,
  getNextBillingEnd,
} from '@/services/utils/billing-format';
import { useStore } from '@/store';
import type { BillingPlan } from '@/services/models';

type PlanAddon = {
  addonName: keyof BillingPlan['limits'];
  addonTotalPrice: number;
};

const props = withDefaults(
  defineProps<{
    estimate: any;
    selected: BillingPlan;
    currencyFormat?: string;
  }>(),
  {
    currencyFormat: '0,0.00',
  },
);

const { t } = useI18n();
const { planLimitsKeyText } = usePlanLimits(undefined, false);
const store = useStore();

const nextBillingAt = computed(() => getNextBillingAt(store.billing));
const nextBillingEnd = computed(() => getNextBillingEnd(store.billing));

const invoiceEstimate = computed(
  () => props.estimate.invoice_estimate || props.estimate.next_invoice_estimate,
);

const addons = computed(() => Object.values(store.billing.addons ?? {}) as PlanAddon[]);

const addonsStay = computed(() =>
  addons.value.filter(addon => props.selected.limits[addon.addonName] !== true),
);

const addonsDeactivated = computed(() =>
  addons.value.filter(addon => props.selected.limits[addon.addonName] === true),
);

const totalShouldBePaidOn = computed(() => {
  const addonsPrice = addonsStay.value.reduce((acc, addon) => {
    return +addon.addonTotalPrice + acc;
  }, 0);
  return addonsPrice + props.selected.price;
});
</script>

<style lang="scss" scoped>
.row {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid var(--color-gray10);
  &:last-child {
    border-bottom: none;
  }
}

.row-title {
  display: flex;
  justify-content: space-between;
  padding: 12px 0 0;
}

.cell {
  font-size: 14px;
}

.change-plan-step2 {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  min-height: 100%;
}
</style>
